import * as React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { config } from 'react-awesome-styled-grid'
import { HeaderSeeAll } from '@components/shared/HeaderSeeAll'
import { ReviewCard } from './ReviewCard'
import { getTrendingReviews } from '../../services/userReviews'
import { getTrackByIdAsync, getCanonicalSlug } from '../../models/Track'
import type { TrackType } from '../../flowTypes'
import type { TrendingReviewType } from '../../services/userReviews'

const MIN_REVIEWS_TO_SHOW = 5

type TrendingReviewsRowPropsType = {
  title: string,
  displayType: 'trending-today',
  description: string | null
}

type ReviewsStateType = {
  status: 'idle' | 'loading' | 'success' | 'error',
  reviews: TrendingReviewType[]
}

export function TrendingReviewsRow ({
  title,
  description
}: TrendingReviewsRowPropsType): React.ReactNode {
  const router = useRouter()
  const [state, setState] = useState<ReviewsStateType>({
    status: 'idle',
    reviews: []
  })

  const handleContentPress = useCallback((contentId: string): void => {
    getTrackByIdAsync(contentId).then((track: TrackType): void => {
      const slug = getCanonicalSlug(track)
      if (slug) {
        router.push(`/chapters/${slug}`)
      }
    })
  }, [router])

  useEffect((): (() => void) => {
    let isMounted = true

    async function fetchReviews (): Promise<void> {
      setState((prev: ReviewsStateType): ReviewsStateType => ({ ...prev, status: 'loading' }))

      try {
        const response = await getTrendingReviews({
          limit: 20,
          timeframe: '30d',
          minFavorites: 5
        })

        if (isMounted) {
          setState({
            status: 'success',
            reviews: response.reviews
          })
        }
      } catch (error) {
        if (isMounted) {
          setState({
            status: 'error',
            reviews: []
          })
        }
      }
    }

    fetchReviews()

    return (): void => {
      isMounted = false
    }
  }, [])

  if (state.status !== 'success') {
    return null
  }

  if (state.reviews.length < MIN_REVIEWS_TO_SHOW) {
    return null
  }

  return (
    <SectionContainer>
      <HeaderSeeAll title={title} description={description} />
      <ReviewsRow>
        {state.reviews.map((review: TrendingReviewType): React.ReactNode => (
          <ReviewCardWrapper key={review.id}>
            <ReviewCard review={review} onContentPress={handleContentPress} />
          </ReviewCardWrapper>
        ))}
      </ReviewsRow>
    </SectionContainer>
  )
}

const SectionContainer = styled.div<any>`
  width: 100%;
`

const ReviewsRow = styled.div<any>`
  display: flex;
  gap: 12px;
  width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  margin: 0;
  padding: 0 0 8px 0;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${(props: any): any => config(props).media.md`
    gap: 16px;
  `}
  ${(props: any): any => config(props).media.lg`
    gap: 20px;
  `}
`

const ReviewCardWrapper = styled.div<any>`
  flex-shrink: 0;
  width: 280px;

  ${(props: any): any => config(props).media.sm`
    width: 300px;
  `}
  ${(props: any): any => config(props).media.md`
    width: 320px;
  `}
  ${(props: any): any => config(props).media.lg`
    width: 340px;
  `}
`
